import { FlaskConical, Smartphone, Award } from "lucide-react";
import { getPcrStatusStyle, getAcrStatusStyle } from "@/lib/statusStyles";

const PCR_INDICATORS = [
  { range: "0 - 0.2", color: "bg-green-500", status: "Healthy", description: "Normal kidney function" },
  { range: "0.21 - 1", color: "bg-yellow-400", status: "Mild", description: "Repeat 3 tests (1 week apart). If 2+ positive, consult doctor" },
  { range: "1 - 3", color: "bg-orange-500", status: "Moderate", description: "Moderate proteinuria - visit nephrologist" },
  { range: ">3", color: "bg-red-600", status: "Heavy", description: "Heavy proteinuria - urgent medical attention" }
];

const ACR_INDICATORS = [
  { range: "<30 mg/g", color: "bg-green-500", status: "Healthy", description: "Normal albumin levels" },
  { range: "30-300 mg/g", color: "bg-yellow-500", status: "Microalbuminuria", description: "Repeat 3 tests (1 week apart). If 2+ positive, consult doctor" },
  { range: ">300 mg/g", color: "bg-red-500", status: "Macroalbuminuria", description: "Significant kidney damage - see nephrologist" }
];

const IndicatorRow = ({ indicator, testIdPrefix, getStatusStyle }) => (
  <div
    data-testid={`${testIdPrefix}-${indicator.status.toLowerCase()}`}
    className="flex items-center gap-3 p-3 rounded-xl bg-slate-100/50 border border-slate-300/50 hover:border-slate-600 transition-colors"
  >
    <div className={`w-3 h-3 rounded-full ${indicator.color} flex-shrink-0`} />
    <div className="flex-1">
      <div className="flex items-center justify-between">
        <span className="text-slate-800 font-semibold text-sm">{indicator.range}</span>
        <span className={`text-xs px-2 py-0.5 rounded ${getStatusStyle(indicator.status)}`}>
          {indicator.status}
        </span>
      </div>
      <p className="text-slate-500 text-xs mt-1">{indicator.description}</p>
    </div>
  </div>
);

export const Science = () => (
  <section id="science" data-testid="science-section" className="relative py-24 md:py-32">
    <div className="absolute inset-0 bg-gradient-to-b from-white via-slate-50 to-white" />

    <div className="relative max-w-7xl mx-auto px-6 md:px-12">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <span className="text-cyan-600 text-sm font-mono uppercase tracking-widest">The Science</span>
        <h2 className="font-display text-3xl md:text-5xl font-bold text-slate-800 mt-4 mb-6">
          Understanding Your <span className="gradient-text">Results</span>
        </h2>
        <p className="text-slate-600 text-lg">
          KidneyCheQ measures both <span className="text-cyan-600 font-semibold">PCR</span> (Protein to Creatinine Ratio) and <span className="text-emerald-600 font-semibold">ACR</span> (Albumin to Creatinine Ratio) - key indicators of kidney health
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        <div className="glass rounded-2xl p-6 md:p-8 border border-cyan-500/20">
          <h3 className="font-display text-xl font-bold text-slate-800 mb-2 flex items-center gap-3">
            <FlaskConical className="w-6 h-6 text-cyan-600" />
            PCR - Protein to Creatinine Ratio*
          </h3>
          <p className="text-slate-500 text-sm mb-6">Measures total protein leakage in urine</p>
          <div className="space-y-3">
            {PCR_INDICATORS.map((indicator) => (
              <IndicatorRow key={indicator.range} indicator={indicator} testIdPrefix="pcr-indicator" getStatusStyle={getPcrStatusStyle} />
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-4">*Reference: KDIGO 2024 Clinical Practice Guideline for CKD</p>
        </div>

        <div className="glass rounded-2xl p-6 md:p-8 border border-emerald-500/20">
          <h3 className="font-display text-xl font-bold text-slate-800 mb-2 flex items-center gap-3">
            <FlaskConical className="w-6 h-6 text-emerald-600" />
            ACR - Albumin to Creatinine Ratio*
          </h3>
          <p className="text-slate-500 text-sm mb-6">Detects early kidney damage via albumin</p>
          <div className="space-y-3">
            {ACR_INDICATORS.map((indicator) => (
              <IndicatorRow key={indicator.range} indicator={indicator} testIdPrefix="acr-indicator" getStatusStyle={getAcrStatusStyle} />
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-4">*Reference: KDIGO 2024 Clinical Practice Guideline for CKD</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="glass rounded-2xl p-6 border border-emerald-500/20">
          <h3 className="font-display text-xl font-bold text-slate-800 mb-4 flex items-center gap-3">
            <Smartphone className="w-5 h-5 text-emerald-600" />
            Sample Result from KidneyCheQ App
          </h3>
          <div className="flex justify-center">
            <img
              src="https://customer-assets.emergentagent.com/job_ominar-hub/artifacts/yeqvd211_WhatsApp%20Image%202026-02-14%20at%2020.06.19.jpeg"
              alt="KidneyCheQ App - Kidney Health Report showing uPCR results"
              className="w-full max-w-xs rounded-xl shadow-lg border border-slate-300"
              data-testid="app-result-screenshot"
            />
          </div>
          <p className="text-slate-600 text-sm text-center mt-4">
            Easy-to-read results with PCR & ACR scores, share with doctor, and download report options
          </p>
        </div>

        <div className="glass rounded-2xl p-8 border border-cyan-500/20">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
              <Award className="w-6 h-6 text-cyan-600" />
            </div>
            <div>
              <h4 className="text-slate-800 font-semibold mb-2">Why Proteinuria Test Matters</h4>
              <p className="text-slate-600 text-sm leading-relaxed">
                Having protein in your urine increases the risk of kidney disease and cardiovascular disease. Kidney disease often shows no signs until the last stage, making regular testing crucial.
              </p>
            </div>
          </div>
          <div className="flex justify-center">
            <div className="max-w-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_1488b547-0470-4710-9082-0fb679cd81f3/artifacts/6wlnad74_albuminuria.avif"
                alt="Albuminuria - Protein in Urine Diagram"
                className="w-full h-auto rounded-lg border border-slate-200 shadow-md"
              />
              <p className="text-xs text-slate-500 mt-3 text-center">
                Image credit: <a href="https://www.kidney.org/kidney-topics/albuminuria-proteinuria" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">National Kidney Foundation</a>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-12 glass rounded-2xl p-8">
        <h3 className="font-display text-2xl font-bold text-slate-800 mb-6 text-center">What We Measure</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-6 rounded-xl bg-slate-100/30 border border-cyan-500/10">
            <h4 className="text-cyan-600 font-semibold mb-3 text-lg">Total Proteins</h4>
            <p className="text-slate-600 text-sm leading-relaxed">
              Proteins are not usually found in urine. Healthy kidneys don't allow proteins to filter into urine from blood. When kidneys are damaged, proteins 'leak' into your urine (proteinuria).
            </p>
          </div>
          <div className="text-center p-6 rounded-xl bg-slate-100/30 border border-emerald-500/10">
            <h4 className="text-emerald-600 font-semibold mb-3 text-lg">Albumin</h4>
            <p className="text-slate-600 text-sm leading-relaxed">
              Albumin is the most common protein in blood. Even small amounts in urine (microalbuminuria) can indicate early kidney damage, especially in diabetics and hypertensive patients.
            </p>
          </div>
          <div className="text-center p-6 rounded-xl bg-slate-100/30 border border-indigo-500/10">
            <h4 className="text-indigo-600 font-semibold mb-3 text-lg">Creatinine</h4>
            <p className="text-slate-600 text-sm leading-relaxed">
              A waste product from muscle tissue breakdown. Healthy kidneys remove creatinine from your blood, which then appears in urine. Used to normalize protein/albumin measurements.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);
